import { TFunction } from 'i18next';
import {
  PapiUser,
  PapiDacheckoutPurchase,
  PapiRequestDacheckoutPurchase,
  PapiOrderV3,
  PapiProductV3,
} from '@wix/da-papi-types';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import { requestNapiAsync } from '@wix/da-http-client/pkg/async';
import { PaymentMethod, CreditCardType } from '../types';
import { BiData, PaymentButtonClickBiEvent } from '@wix/da-bi/pkg/events';
import { API_BASE_PATH } from '../../constants';

export const getOrderV3TotalPrice = (
  method: PaymentMethod,
  order: PapiOrderV3
) => {
  return method === PaymentMethod.POINTS
    ? order.product.price.points
    : order.product.price.usd;
};

export const getOrderV3TotalPriceInUSD = (order: PapiOrderV3) =>
  order.product.price.usd;

export const getOrderV3TotalPriceInPoints = (order: PapiOrderV3) =>
  order.product.price.points;

export const getOrderV3ProductPriceBeforeDiscount = (
  method: PaymentMethod,
  order: PapiOrderV3
) => {
  return method === PaymentMethod.POINTS
    ? order.product.discount?.priceBeforeDiscount?.points ||
        order.product.price.points ||
        0
    : order.product.discount?.priceBeforeDiscount?.usd ||
        order.product.price.usd ||
        0;
};

export const getOrderV3ProductPriceBeforeDiscountInUSD = (
  order: PapiOrderV3
) => {
  return (
    order.product.discount?.priceBeforeDiscount?.usd ||
    order.product.price.usd ||
    0
  );
};

export const getOrderV3ProductPriceBeforeDiscountInPoints = (
  order: PapiOrderV3
) => {
  return (
    order.product.discount?.priceBeforeDiscount?.points ||
    order.product.price.points ||
    0
  );
};

export const getOrderDiscountV3 = (
  method: PaymentMethod,
  order: PapiOrderV3
) => {
  return method === PaymentMethod.POINTS
    ? order.product.discount?.amount.points || 0
    : order.product.discount?.amount.usd || 0;
};

export const getOrderV3DiscountInUSD = (order: PapiOrderV3) =>
  order.product.discount?.amount.usd || 0;

export const getOrderV3DiscountInPoints = (order: PapiOrderV3) =>
  order.product.discount?.amount.points || 0;

export const getDiscountCampaignLabel = (order: PapiOrderV3) =>
  order.product.discount?.campaignLabel;

export const getDiscountPercent = (order: PapiOrderV3) =>
  order.product.discount?.amount.percent || 0;

export const getOrderHasDiscount = (order: PapiOrderV3) =>
  !!order.product.discount?.amount.usd;

export const formatPrice = (
  t: TFunction,
  method: PaymentMethod,
  price: number
) => {
  return method === PaymentMethod.POINTS
    ? t('price.points', {
        price: Number(price).toLocaleString(),
      })
    : t('price.usd', {
        price: formatDollars(price),
      });
};

// Code ported from green site
export const getCreditCardType = (
  number: string
): CreditCardType | undefined => {
  if (!number || !number.match(/^\d+$/)) {
    return undefined;
  }
  if (number[0] === '4' && (number.length === 13 || number.length === 16)) {
    return CreditCardType.VISA;
  }
  const prefix2 = number.substr(0, 2);
  if ((prefix2 === '34' || prefix2 === '37') && number.length === 15) {
    return CreditCardType.AMEX;
  }
  const prefix2int = parseInt(prefix2, 10);
  if (
    ((prefix2int >= 51 && prefix2int <= 55) ||
      (prefix2int >= 22 && prefix2int <= 27)) &&
    number.length === 16
  ) {
    return CreditCardType.MASTERCARD;
  }
  if (number.substr(0, 4) === '6011' && number.length === 16) {
    return CreditCardType.DISCOVER;
  }
};

export const isValidCreditCardNumber = (number: string) => {
  number = sanitizeCreditCardNumber(number);
  if (!getCreditCardType(number)) {
    return false;
  }
  // Luhn check, code ported from green site
  let sum = 0;
  for (let i = number.length - 1; i >= 0; i -= 2) {
    sum += parseInt(number[i], 10);
  }
  for (let i = number.length - 2; i >= 0; i -= 2) {
    let doubled = parseInt(number[i], 10) * 2;
    if (doubled >= 10) {
      doubled -= 9;
    }
    sum += doubled;
  }
  return sum % 10 === 0;
};
export const sanitizeCreditCardNumber = (number: string) => {
  return number.replace(/[^0-9]/g, '');
};

export const getOrderedProductTypeV3 = (
  order: PapiOrderV3
): PapiProductV3['productType'] => order.product.productType;

const getFlowType = (
  isCorePurchase: boolean,
  hasCoreMembership: boolean,
  isGift: boolean
) => {
  if (!isCorePurchase) {
    return undefined;
  }

  if (isGift) {
    return 'give_core';
  }

  if (hasCoreMembership) {
    return 'upgrade_core';
  }

  return 'become_core';
};

export const getPayBiDataV3 = (
  order: PapiOrderV3,
  paymentMethod: PaymentMethod,
  hasCoreMembership: boolean,
  giftUser?: PapiUser
) => {
  const itemsIds = [order.product.subproductId];
  const flowType = getFlowType(
    getOrderedProductTypeV3(order) === 'core',
    hasCoreMembership,
    giftUser !== undefined
  );

  return BiData<PaymentButtonClickBiEvent>({
    evid: 520,
    payment_type: paymentMethod as any,
    item_total: order.product.price.usd,
    num_items: itemsIds.length,
    subproductid: itemsIds.join(','),
    give_core_or_become_core: flowType as any,
    is_form_valid: 1,
  });
};

export function placeOrderAsync(
  data: PapiRequestDacheckoutPurchase,
  csrfToken: string
): Promise<PapiDacheckoutPurchase> {
  // /purchase is still proxied through node so we call NAPI here
  return requestNapiAsync(
    {
      method: 'post',
      baseURL: API_BASE_PATH,
      url: '/purchase',
      data,
    },
    { csrfToken }
  );
}
